import React, { useState } from 'react';
import { PageHeader } from '@/ui/PageHeader';
import { NotesBoard } from '@/components/NotesBoard';
import { devTimelineCommits, DEV_TIMELINE_SNAPSHOT_DATE } from '@/data/devTimelineCommits';
import { HANDOFFS, type Handoff } from '@/data/handoffs';

const STATUS_OPTIONS = ['planned', 'in_progress', 'shipped', 'blocked'];

const HANDOFF_STATUS: Record<Handoff['status'], { label: string; tone: string }> = {
  ready: { label: 'Ready to hand off', tone: 'admin-status-accent' },
  handed_off: { label: 'Handed off', tone: 'admin-status-warning' },
  in_progress: { label: 'In progress', tone: 'admin-status-warning' },
  shipped: { label: 'Shipped', tone: 'admin-status-success' },
};

/** One project module: what it is, plus the paste-ready prompt for the master agent. */
function HandoffModule({ handoff }: { handoff: Handoff }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const meta = HANDOFF_STATUS[handoff.status];

  const copy = () => {
    void navigator.clipboard
      .writeText(handoff.prompt)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        /* clipboard unavailable — the prompt is selectable below */
      });
  };

  return (
    <div className="admin-card !p-0 overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3.5">
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold text-textPrimary">{handoff.title}</div>
        </div>
        <span className={`admin-status ${meta.tone}`}>{meta.label}</span>
        <button type="button" onClick={copy} className="admin-btn admin-btn-primary admin-btn-sm">
          {copied ? 'Copied' : 'Copy prompt'}
        </button>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="admin-btn admin-btn-secondary admin-btn-sm"
        >
          {open ? 'Hide' : 'View'}
        </button>
      </div>
      <p className="border-t border-border/60 px-4 py-3 text-[12.5px] leading-relaxed text-muted">
        {handoff.summary}
      </p>
      {open ? (
        <pre className="max-h-[420px] overflow-auto border-t border-border/60 bg-canvas px-4 py-3.5 text-[11.5px] leading-relaxed text-muted whitespace-pre-wrap">
          {handoff.prompt}
        </pre>
      ) : null}
    </div>
  );
}

export function DevelopmentScreen() {
  return (
    <div className="admin-page max-w-5xl mx-auto">
      <PageHeader
        title="Development"
        subtitle="The big builds in flight — each module explains the project and holds the paste-ready handoff prompt for the master agent."
      />

      <div className="mb-8 space-y-3">
        <div className="admin-eyebrow">Active handoffs</div>
        {HANDOFFS.map((h) => (
          <HandoffModule key={h.id} handoff={h} />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <section className="min-w-0">
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <div className="admin-eyebrow">Recent commits</div>
            <span className="text-[11px] tabular-nums text-mutedDim">{devTimelineCommits.length} shown</span>
          </div>
          <p className="mb-3 text-[11px] leading-relaxed text-mutedDim">
            Snapshot as of {DEV_TIMELINE_SNAPSHOT_DATE} — regenerate <code>src/data/devTimelineCommits.ts</code> from{' '}
            <code>git log</code> to refresh. Not live.
          </p>
          <div className="admin-card max-h-[70vh] overflow-y-auto divide-y divide-border/50">
            {devTimelineCommits.map((c) => (
              <div key={c.hash} className="flex gap-3 py-2.5">
                <div className="w-20 shrink-0 pt-0.5 text-[11px] tabular-nums text-mutedDim">{c.date}</div>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] leading-snug text-textPrimary">{c.subject}</p>
                  <p className="mt-0.5 text-[11px] text-mutedDim">
                    <span className="admin-chip-id">{c.hash.slice(0, 7)}</span>{' '}
                    <span className="ml-1.5">{c.author}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="min-w-0">
          <div className="mb-3 admin-eyebrow">Planned / roadmap</div>
          <NotesBoard
            page="dev_timeline"
            statusOptions={STATUS_OPTIONS}
            newButtonLabel="Add roadmap item"
            emptyTitle="No roadmap items yet"
            emptyHint="Add the first planned item above."
          />
        </section>
      </div>
    </div>
  );
}
